import React, { useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { GenericModal } from '..';
import { useTranslation } from 'react-i18next';
import SecondStepModal from './second-step-modal.component';
import { submitUserOnboardingApi, UserOnboardingPayload } from '../../../features/onboarding/onboarding.service';
import { PersonalIdInput, DateOfBirthInput } from '../../common';
import './business-profile-modal.style.scss';
import { getItem } from '../../../helper/localstorage.helper';
import { useSelector } from 'react-redux';
import { isGuestInvozioEmail, validateEmail } from '../../../utils/registration-validation';

interface BusinessProfileModalProps {
  onClose: () => void;
  onNext?: () => void;
  onStartTour: () => void;
  show: boolean;
}

interface BusinessFormData {
  personalId: string;
  noIdNumber: boolean;
  dateOfBirth: string;
  businessName: string;
  companyRegistrationNumber: string;
  vatNumber: string;
  city: string;
  country: string;
  profession: string;
  businessAddress: string;
  postalCode: string;
  email?: string;
}

interface ValidationErrors {
  personalId?: string;
  dateOfBirth?: string;
  businessName?: string;
  companyRegistrationNumber?: string;
  city?: string;
  businessAddress?: string;
  postalCode?: string;
  email?: string;
  general?: string;
}

const BusinessProfileModal: React.FC<BusinessProfileModalProps> = ({
  onClose,
  onNext,
  onStartTour,
  show
}) => {
  const { t } = useTranslation();
  const [showSecondStep, setShowSecondStep] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [DOBValue, setDOBValue] = useState('');
  const { userOnboardingData } = useSelector((state: any) => state.auth);
  const localstorageUser = JSON.parse(getItem('user') as string);
  const user = localstorageUser;
  const isLoginWithBankId = localstorageUser?.isLoginWithBankId || false;
  const [showSecondModal, setShowSecondModal] = useState(false);
  const [formData, setFormData] = useState<BusinessFormData>({
    personalId: '',
    noIdNumber: false,
    dateOfBirth: '',
    businessName: '',
    companyRegistrationNumber: '',
    vatNumber: '',
    city: '',
    profession: '',
    country: '',
    businessAddress: '',
    postalCode: '',
    email: ''
  });

  const resetModal = () => {
    setFormData({
      personalId: '',
      noIdNumber: false,
      dateOfBirth: '',
      businessName: '',
      companyRegistrationNumber: '',
      vatNumber: '',
      profession: '',
      country: '',
      city: '',
      businessAddress: '',
      postalCode: '',
      email: ''
    });
    setValidationErrors({});
    setIsLoading(false);
    setShowSecondStep(false);
    setDOBValue('');
  };
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    // Required fields validation
    if (!formData.businessName.trim()) {
      errors.businessName = 'BUSINESS_NAME_REQUIRED';
    }

    if (!formData.companyRegistrationNumber.trim()) {
      errors.companyRegistrationNumber = 'COMPANY_REGISTRATION_REQUIRED';
    }

    if (!formData.city.trim()) {
      errors.city = 'CITY_REQUIRED';
    }

    if (!formData.businessAddress.trim()) {
      errors.businessAddress = 'BUSINESS_ADDRESS_REQUIRED';
    }

    if (!formData.postalCode.trim()) {
      errors.postalCode = 'POSTAL_CODE_REQUIRED';
    }

    // Require email for BankID users
    if (isLoginWithBankId && !formData.email?.trim()) {
      errors.email = 'EMAIL_REQUIRED';
    } else if (isLoginWithBankId && formData.email?.trim()) {
      if (!validateEmail(formData.email)) {
        errors.email = 'EMAIL_INVALID';
      } else if (isGuestInvozioEmail(formData.email)) {
        errors.email = 'GUEST_EMAIL_NOT_ALLOWED';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    setValidationErrors(prev => ({
      ...prev,
      general: undefined
    }));

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload: UserOnboardingPayload = {
        personalId: userOnboardingData.personalId,
        firstName: userOnboardingData?.firstName,
        email: isLoginWithBankId ? formData.email : userOnboardingData.email,
        companyRegistrationNumber: formData.companyRegistrationNumber,
        address: formData.businessAddress,
        vatNumber: formData.vatNumber,
        ssn: userOnboardingData.ssn,
        city: formData.city,
        country: userOnboardingData.country,
        profession: formData.profession,
        zipCode: formData.postalCode,
        dateOfBirth: userOnboardingData.dateOfBirth || "",
        password: userOnboardingData.password,
        phoneNo: userOnboardingData.phoneNo,
        accountType: userOnboardingData.accountType, 
      };

      const response = await submitUserOnboardingApi(payload);

      if (response.statusCode === 200 || response.statusCode === 201) {
        if (onNext) {
          onNext();
        }
        setShowSecondStep(true);
      } else {
        setValidationErrors(prev => ({
          ...prev,
          general: response.message || 'Something went wrong. Please try again.'
        }));
      }
              setShowSecondModal(true);
    } catch (error: any) {
      setValidationErrors(prev => ({
        ...prev,
        general: error?.response?.data?.message || 'Network error. Please check your connection and try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecondStepClose = () => {
    setShowSecondStep(false);
    resetModal();
    onClose();
  };

  const handleModalClose = () => {
    resetModal();
    onClose();
  };

  const handleInputChange = (field: keyof BusinessFormData, value: string | boolean) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // If toggling noIdNumber checkbox
      if (field === 'noIdNumber') {
        if (value === false) {
          // When unchecking, clear the date of birth
          newData.dateOfBirth = '';
        }
      }

      return newData;
    });

    // Clear validation error when user starts typing
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }

    // Reset DOB value when toggling checkbox
    if (field === 'noIdNumber') {
      if (value === false) {
        setDOBValue('');
      }
    }
  };

  return (
    <>
      <div className='business-profile-container-main'>
        <GenericModal
          show={show && !showSecondStep}
          onClose={handleModalClose}
          size="xl"
          showFooter={false}
          showCloseButton={false}
          closeOnBackdrop={true}
          className="business-profile-modal"
        >
          <div className="business-profile-content">
            {/* Header */}
            <div className="business-profile-header">
              <h2 className="business-profile-title">{t("COMPLETE_YOUR_PROFILE")}</h2>
              <button
                className="business-profile-close-btn"
                onClick={handleModalClose}
                aria-label="Close"
              >
                <AiOutlineClose size={32} color="#252525"   className='mb-2'/>
              </button>
            </div>

            {/* Form Content */}
            <div className="business-profile-body">
              {/* General Error Message */}
              {validationErrors.general && (
                <div className="error-message general-error" style={{ marginBottom: '16px', color: '#dc3545', fontWeight: '500' }}>
                  {validationErrors.general}
                </div>
              )}

              {/* Email Field - Only for BankID users */}
              {isLoginWithBankId && (
                <div className="form-field" style={{ marginBottom: '16px' }}>
                  <label htmlFor="email">{t("EMAIL")}</label>
                  <input
                    id="email"
                    type="email"
                    placeholder={t('ENTER_EMAIL') || 'Enter your email'}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={validationErrors.email ? 'error' : ''}
                  />
                  {validationErrors.email && (
                    <span className="error-message">{t(validationErrors.email)}</span>
                  )}
                </div>
              )}

              {/* Second Row */}
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="businessName">{t("BUSINESS_NAME")}</label>
                  <input
                    id="businessName"
                    type="text"
                    placeholder={t('ENTER_BUSINESS_NAME')}
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    className={validationErrors.businessName ? 'error' : ''}
                  />
                  {validationErrors.businessName && (
                    <span className="error-message">{t(validationErrors.businessName)}</span>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="companyRegistrationNumber">{t("COMPANY_REGISTRATION_NUMBER")}</label>
                  <input
                    id="companyRegistrationNumber"
                    type="text"
                    placeholder={t('ENTER_COMPANY_REGISTRATION_NUMBER')}
                    value={formData.companyRegistrationNumber}
                    onChange={(e) => handleInputChange('companyRegistrationNumber', e.target.value)}
                    className={validationErrors.companyRegistrationNumber ? 'error' : ''}
                  />
                  {validationErrors.companyRegistrationNumber && (
                    <span className="error-message">{t(validationErrors.companyRegistrationNumber)}</span>
                  )}
                </div>
              </div>

              {/* Third Row */}
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="vatNumber">{t("VAT_NUMBER")}</label>
                  <input
                    id="vatNumber"
                    type="text"
                    placeholder={t('ENTER_COMPANY_VAT_NUMBER')}
                    value={formData.vatNumber}
                    onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="city">{t("CITY")}</label>
                  <input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className={validationErrors.city ? 'error' : ''}
                  >
                  </input>
                  {validationErrors.city && (
                    <span className="error-message">{t(validationErrors.city)}</span>
                  )}
                </div>
              </div>

              {/* Fourth Row */}
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="businessAddress">{t("BUSINESS_ADDRESS")}</label>
                  <input
                    id="businessAddress"
                    type="text"
                    placeholder={t('ENTER_BUSINESS_ADDRESS')}
                    value={formData.businessAddress}
                    onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                    className={validationErrors.businessAddress ? 'error' : ''}
                  />
                  {validationErrors.businessAddress && (
                    <span className="error-message">{t(validationErrors.businessAddress)}</span>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="postalCode">{t("POSTAL_CODE")}</label>
                  <input
                    id="postalCode"
                    type="text"
                    placeholder={t('ENTER_POSTAL_CODE')}
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    className={validationErrors.postalCode ? 'error' : ''}
                  />
                  {validationErrors.postalCode && (
                    <span className="error-message">{t(validationErrors.postalCode)}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="business-profile-footer">
              {/* Progress Dots */}
              <div className="progress-dots">
                <div className="dot"></div>
                <div className="dot active"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>

              {/* Next Button */}
              <button
                className="business-next-btn"
                onClick={handleNext}
                disabled={isLoading}
                style={{
                  opacity: isLoading ? 0.6 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {isLoading ? t('PROCESSING') : t('NEXT')}
              </button>
            </div>
          </div>
        </GenericModal>
      </div>

      {/* Second Step Modal */}
      <SecondStepModal onStartTour={() => {
        setShowSecondModal(false);
        onStartTour();
      }
      } show={showSecondModal} onClose={() => {
        // Cross on the final step must fully close onboarding, not fall back
        // to the welcome step (which would leave the welcome modal open
        // behind). onNext === closeAllModals in the onboarding flow.
        setShowSecondModal(false);
        onNext ? onNext() : onClose();
      }} />

    </>
  );
};

export default BusinessProfileModal;
